import { ElementType } from "react";
import { FaCaretDown } from "react-icons/fa";

import { cn } from "@bentolabs/kit/utils";
import { cva, VariantProps } from "class-variance-authority";

import currencyPlaceholderSvg from "shared/assets/currency-placeholder.svg";
import { PolymorphicProps } from "shared/types";
import { Img } from "shared/ui/img";

const variants = cva(
  [
    "relative transition-colors cursor-default flex shrink-0 items-center outline-hidden",
  ],
  {
    variants: {
      variant: {
        button: ["rounded-[10px] p-2 cursor-pointer"],
        normal: ["p-0"],
      },
      color: {
        "nobel/10": [
          "bg-nobel/10 text-white",
          "hover:bg-nobel/20",
          "active:bg-nobel/20",
          "focus:bg-nobel/20",
        ],
        transparent: ["bg-transparent text-white"],
      },
      size: {
        sm: "text-sm gap-x-2 leading-none",
        lg: "text-lg gap-x-3 leading-[105%]",
      },
      disabled: {
        true: "pointer-events-none cursor-default",
        false: null,
      },
    },
    defaultVariants: {
      variant: "button",
      color: "nobel/10",
      size: "sm",
      disabled: false,
    },
  }
);

const iconVariants = cva([], {
  variants: {
    size: {
      sm: "size-5",
      lg: "size-8",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

type CurrencyLabelVariantsProps = VariantProps<typeof variants>;

type Props = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  logoURI?: string;
  symbol?: string;
  showIcon?: boolean;
} & CurrencyLabelVariantsProps;

export function Label<E extends ElementType = "button">(
  props: PolymorphicProps<E, Props>
) {
  const {
    as: Comp = "div",
    iconClassName,
    children,
    logoURI,
    symbol,
    disabled,
    className,

    size,
    variant,
    color,
    onClick,
    showIcon,

    ...buttonProps
  } = props;

  return (
    <Comp
      className={cn(variants({ size, variant, color, className, disabled }))}
      onClick={onClick}
      {...buttonProps}
    >
      <Img
        src={logoURI}
        className={cn(iconVariants({ size }), iconClassName)}
        fallbackUrl={currencyPlaceholderSvg}
        alt={symbol}
      />
      <span>{symbol}</span>
      {children}
      {onClick && showIcon && (
        <FaCaretDown className="text-nobel/50 ml-auto size-4" />
      )}
    </Comp>
  );
}
